import { useReducer, useEffect } from 'react';
import { shallowEqualObjects } from 'shallow-equal';

function reducer<T extends object, K extends T>(
  oldState: T,
  newState: K | ((arg: T) => K),
): T {
  if (typeof newState === 'function') {
    newState = newState(oldState);
    if (newState === null) {
      return oldState;
    }
  }
  Object.keys(newState).forEach((key) => {
    if (!Object.prototype.hasOwnProperty.call(oldState, key)) {
      console.error(`There is no key '${key}' in the oldState`); // eslint-disable-line no-console
    }
  });
  if (shallowEqualObjects(oldState, newState)) {
    return oldState;
  }
  return { ...oldState, ...newState };
}

type AtLeastOne<T> = { [K in keyof T]: Pick<T, K> }[keyof T] & Partial<T>;
export default function useStateReducer<
  T extends object,
  K extends AtLeastOne<T> = AtLeastOne<T>,
>(iState: T, deps = []): [T, (arg: K | ((arg1: T) => K | null)) => void] {
  const [state, setState] = useReducer(reducer, iState);
  useEffect(() => {
    if (deps.length > 0) {
      setState(iState);
    }
  }, deps);
  return [state, setState];
}
